import { useState } from 'react';
import { H2 } from '@blueprintjs/core';
import { Button } from '@launchpad-ui/components';

import { Alert, BooleanTag, DataList, DataListItem } from '../../../shared';
import { Box, Flex } from '../../../shared/components';
import { isProduction } from '../../../shared/utils';
import {
  useClearPendingSubscriptionUpdate,
  useUpdateOrbSubscriptionId,
  useUpdateStripeSubscriptionId,
} from '../../hooks/useUpdateSubscription';
import { type Account, type Subscription } from '../../model';
import { OrbLink } from '../OrbLink';

import './SubscriptionDetails.scss';

type SubscriptionDetailsProps = Readonly<{
  account: Account;
  subscription: Subscription;
}>;

export function SubscriptionDetails({ account, subscription }: SubscriptionDetailsProps) {
  const [stripeSubscriptionId, setStripeSubscriptionId] = useState('');
  const [orbSubscriptionId, setOrbSubscriptionId] = useState('');
  const [isStripeConfirmOpen, setIsStripeConfirmOpen] = useState(false);
  const [isOrbConfirmOpen, setIsOrbConfirmOpen] = useState(false);

  const updateStripeMutation = useUpdateStripeSubscriptionId(account);
  const updateOrbMutation = useUpdateOrbSubscriptionId(account);
  const clearPendingMutation = useClearPendingSubscriptionUpdate(account);

  const handleUpdateStripeSubscriptionId = () => {
    updateStripeMutation.mutate({ subscription, stripeSubscriptionId });
    setStripeSubscriptionId('');
  };

  const handleUpdateOrbSubscriptionId = () => {
    updateOrbMutation.mutate({ subscription, orbSubscriptionId });
    setOrbSubscriptionId('');
  };

  const handleClearPendingUpdate = () => {
    clearPendingMutation.mutate({ subscription });
  };

  return (
    <>
      <Flex>
        <Box>
          <H2>Billing subscription</H2>
        </Box>
      </Flex>
      <DataList>
        <DataListItem>
          <Flex align="center" className="subscription-details__row">
            <Box className="subscription-details__label">Pending Update</Box>
            <Box className="subscription-details__value">
              <BooleanTag
                value={!!subscription.pendingUpdate}
                tooltip={subscription.pendingUpdate ? 'true' : 'false'}
              />
            </Box>
            <Box className="subscription-details__spacer" />
            <Box>
              {subscription.pendingUpdate && (
                <Button
                  variant="destructive"
                  onPress={handleClearPendingUpdate}
                  isDisabled={clearPendingMutation.isPending}
                >
                  {clearPendingMutation.isPending ? 'Clearing...' : 'Clear'}
                </Button>
              )}
            </Box>
          </Flex>
        </DataListItem>
        <DataListItem>
          <Flex align="center" className="subscription-details__row">
            <Box className="subscription-details__label">Stripe Subscription Id</Box>
            <Box className="subscription-details__value">
              {subscription.stripeSubscriptionId ? (
                <a
                  href={`${isProduction() ? 'https://dashboard.stripe.com' : 'https://dashboard.stripe.com/test'}/subscriptions/${subscription.stripeSubscriptionId}`}
                >
                  {subscription.stripeSubscriptionId}
                </a>
              ) : (
                <span>None</span>
              )}
            </Box>
            <Box className="subscription-details__input-container">
              <input
                onChange={(e) => setStripeSubscriptionId(e.target.value)}
                value={stripeSubscriptionId}
                type="text"
                placeholder="Enter subscription ID"
                className="subscription-details__input"
              />
            </Box>
            <Box>
              <Button
                variant="primary"
                onPress={() => setIsStripeConfirmOpen(true)}
                isDisabled={updateStripeMutation.isPending}
              >
                {updateStripeMutation.isPending ? 'Updating...' : 'Update'}
              </Button>
              <Alert
                intent="warning"
                isOpen={isStripeConfirmOpen}
                onClose={() => setIsStripeConfirmOpen(false)}
                onConfirm={handleUpdateStripeSubscriptionId}
                onCancel={() => setIsStripeConfirmOpen(false)}
                confirmButtonText="Update"
                title="Update Stripe subscription ID"
              >
                Are you sure you want to update the Stripe subscription ID
                {stripeSubscriptionId ? ` to "${stripeSubscriptionId}"` : ''}?
              </Alert>
            </Box>
          </Flex>
        </DataListItem>
        <DataListItem>
          <Flex align="center" className="subscription-details__row">
            <Box className="subscription-details__label">Orb Subscription Id</Box>
            <Box className="subscription-details__value">
              {subscription.orbSubscriptionId ? (
                <OrbLink orbSubscriptionId={subscription.orbSubscriptionId} account={account}>
                  {subscription.orbSubscriptionId}
                </OrbLink>
              ) : (
                <span>None</span>
              )}
            </Box>
            <Box className="subscription-details__input-container">
              <input
                onChange={(e) => setOrbSubscriptionId(e.target.value)}
                value={orbSubscriptionId}
                type="text"
                placeholder="Enter subscription ID"
                className="subscription-details__input"
              />
            </Box>
            <Box>
              <Button
                variant="primary"
                onPress={() => setIsOrbConfirmOpen(true)}
                isDisabled={updateOrbMutation.isPending}
              >
                {updateOrbMutation.isPending ? 'Updating...' : 'Update'}
              </Button>
              <Alert
                intent="warning"
                isOpen={isOrbConfirmOpen}
                onClose={() => setIsOrbConfirmOpen(false)}
                onConfirm={handleUpdateOrbSubscriptionId}
                onCancel={() => setIsOrbConfirmOpen(false)}
                confirmButtonText="Update"
                title="Update Orb subscription ID"
              >
                Are you sure you want to update the Orb subscription ID
                {orbSubscriptionId ? ` to "${orbSubscriptionId}"` : ''}?
              </Alert>
            </Box>
          </Flex>
        </DataListItem>
      </DataList>
    </>
  );
}
